import type { ReactNode } from 'react'
import { motion, Transition } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { ResizableDiv } from '@highlight/ui'
import { useIsMiniPaused } from '../../../atoms'
import { useIsMiniAnchoredLeft } from '../side-context'
import { useMiniContentHover } from '../use-mini-content-hover'

const miniLayoutTransition: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.4,
}

const boxShadowLeft = {
  boxShadow:
    '-1px 0.5px 1px -0.5px rgba(0, 0, 0, 0.08), -3px 1.5px 3px -1.5px rgba(0, 0, 0, 0.04), -6px 3px 6px -3px rgba(0, 0, 0, 0.04), -12px 6px 12px -6px rgba(0, 0, 0, 0.04)',
}

const boxShadowRight = {
  boxShadow:
    '1px 0.5px 1px -0.5px rgba(0, 0, 0, 0.08), 3px 1.5px 3px -1.5px rgba(0, 0, 0, 0.04), 6px 3px 6px -3px rgba(0, 0, 0, 0.04), 12px 6px 12px -6px rgba(0, 0, 0, 0.04)',
}

interface MiniContentProps {
  children?: ReactNode
  className?: string
}

export function MiniContent({ children, className }: MiniContentProps) {
  const isOnLeftSide = useIsMiniAnchoredLeft()
  const isPaused = useIsMiniPaused()
  const hoverHandlers = useMiniContentHover()

  return (
    <div
      className={twMerge('-mb-1.5 -mt-1.5 pb-1.5 pt-1.5', isOnLeftSide ? '-mr-1.5 pr-1.5' : '-ml-1.5 pl-1.5')}
      {...hoverHandlers}
    >
      <motion.div
        transition={miniLayoutTransition}
        className={twMerge(
          'z-10 flex h-8 min-w-8 shrink-0 items-center justify-center overflow-hidden px-[3px]',
          'from-background/80 to-background/100 border bg-gradient-to-b',
          'dark:from-foreground/5 dark:to-foreground/10',
          isPaused && 'bg-background-subtle border-border',
          // CSS transitions for border-radius and box-shadow (cheaper than Framer Motion)
          'transition-[border-radius,box-shadow] duration-200',
          isOnLeftSide
            ? 'rounded-bl-[8px] rounded-br-[16px] rounded-tl-[8px] rounded-tr-[16px]'
            : 'rounded-bl-[16px] rounded-br-[8px] rounded-tl-[16px] rounded-tr-[8px]',
          className,
        )}
        style={isOnLeftSide ? boxShadowLeft : boxShadowRight}
        initial={false}
      >
        <ResizableDiv
          className={twMerge('overflow-visible', !isOnLeftSide && 'flex justify-end')}
          // shrink-0: docked right, the clip is a `justify-end` flex box while its width springs open,
          // and a shrinkable child would be squeezed to that width instead of just clipped.
          containerClassName={twMerge('flex shrink-0 items-center', !isOnLeftSide && 'flex-row-reverse')}
        >
          {children}
        </ResizableDiv>
      </motion.div>
    </div>
  )
}
